import { useState, useEffect } from 'react';
import { ref, onValue, get } from 'firebase/database';
import { db } from '../config/firebase';
import { TripMeta } from '../types/trip';

/**
 * Fetches trip metadata for all trips a user belongs to.
 * Subscribes to users/{uid}/tripIds, then fetches each trip's meta in parallel.
 *
 * Scale note: With 20+ trips, this fires N parallel get() calls on each tripIds
 * change. Acceptable for Firebase RTDB (reads are cheap, parallel is fast), but
 * if trip count grows past ~50, consider a denormalized user-trip-list node that
 * stores meta summaries to reduce to a single read.
 */
export function useUserTrips(uid: string | undefined) {
  const [data, setData] = useState<TripMeta[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!uid) {
      setData([]);
      setIsLoading(false);
      return;
    }

    const tripIdsRef = ref(db, `users/${uid}/tripIds`);
    const unsubscribe = onValue(
      tripIdsRef,
      async (snapshot) => {
        const tripIds = snapshot.val();
        if (!tripIds) {
          setData([]);
          setIsLoading(false);
          return;
        }

        try {
          const metas = await Promise.all(
            Object.keys(tripIds).map(async (tripId) => {
              const metaSnap = await get(ref(db, `trips/${tripId}/meta`));
              return metaSnap.val() as TripMeta;
            })
          );
          setData(metas.filter((m) => m && !m.deleted));
        } catch (err) {
          setError(err instanceof Error ? err : new Error('Failed to load trips'));
        }
        setIsLoading(false);
      },
      (err) => {
        setError(err);
        setIsLoading(false);
      }
    );
    return () => unsubscribe();
  }, [uid]);

  return { data, isLoading, error };
}
